import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * EditorCoachTour - Geführte Spotlight-Tour im Editor (#757)
 *
 * Dunkelt den Editor ab, stanzt das gerade erklärte Bedienelement frei und
 * zeigt daneben ein ruhiges Popover mit „Weiter/Zurück" und einem globalen
 * „Tour überspringen" (für Wiederkehrer, die die Tour kennen).
 *
 * Anker werden über `data-tour-id` im DOM gefunden und live ausgemessen —
 * robust gegen Layout/Touch. Fehlt ein Anker, zeigt der Schritt sein Popover
 * mittig ohne Spotlight, statt die Tour abzubrechen.
 *
 * onClose(reason): 'done' (durchgeklickt) | 'skipped' (Esc/Überspringen).
 */
const HOLE_PADDING = 8;
const EDGE_PADDING = 12;
const ANCHOR_GAP = 14;
const POPOVER_WIDTH = 320;

function readAnchorRect(anchorId) {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector(`[data-tour-id="${anchorId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom };
}

const EditorCoachTour = ({ steps, onClose }) => {
  const [index, setIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState(() => readAnchorRect(steps[0].anchorId));
  const [pos, setPos] = useState({ top: 0, left: 0, centered: true });
  const popoverRef = useRef(null);
  const primaryBtnRef = useRef(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;

  const finish = useCallback((reason) => onClose(reason), [onClose]);

  const measure = useCallback(() => setAnchorRect(readAnchorRect(step.anchorId)), [step.anchorId]);

  // Schrittwechsel misst den Ziel-Anker sofort mit (im selben State-Update wie der
  // Index), damit das Spotlight-Loch nie einen Frame lang am alten Anker stehen
  // bleibt (sichtbares Aufblitzen, #757). Die Anker liegen alle im DOM, sobald die
  // Tour läuft (Garten-Tab, 2D), also ist die Messung hier verlässlich.
  const goTo = (nextIndex) => {
    setAnchorRect(readAnchorRect(steps[nextIndex].anchorId));
    setIndex(nextIndex);
  };

  // Nach dem Commit (Mount + Schrittwechsel) nachmessen und mit Layout-Änderungen
  // synchron halten (Resize/Scroll in inneren Containern — capture-Phase). setState
  // läuft im rAF-Callback, nicht synchron im Effect-Body.
  useEffect(() => {
    let raf = 0;
    const onLayout = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    onLayout();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [measure]);

  // Popover relativ zum Anker platzieren und in den Viewport klemmen.
  useLayoutEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pop = popoverRef.current;
    const ph = pop?.offsetHeight ?? 160;
    const pw = pop?.offsetWidth ?? POPOVER_WIDTH;

    if (!anchorRect) {
      setPos({ top: Math.max(EDGE_PADDING, (vh - ph) / 2), left: Math.max(EDGE_PADDING, (vw - pw) / 2), centered: true });
      return;
    }

    let top;
    let left;
    if (step.placement === 'right') {
      left = anchorRect.right + ANCHOR_GAP;
      top = anchorRect.top;
      // Kein Platz rechts → unter den Anker ausweichen
      if (left + pw > vw - EDGE_PADDING) {
        left = anchorRect.left;
        top = anchorRect.bottom + ANCHOR_GAP;
      }
    } else {
      // 'bottom' (Standard): unter den Anker, sonst darüber
      left = anchorRect.left;
      top = anchorRect.bottom + ANCHOR_GAP;
      if (top + ph > vh - EDGE_PADDING) {
        top = anchorRect.top - ANCHOR_GAP - ph;
      }
    }

    left = Math.min(Math.max(EDGE_PADDING, left), vw - pw - EDGE_PADDING);
    top = Math.min(Math.max(EDGE_PADDING, top), vh - ph - EDGE_PADDING);
    setPos({ top, left, centered: false });
  }, [anchorRect, index, step.placement]);

  // Esc = überspringen; Fokus auf den Primär-Button je Schritt.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish('skipped');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [finish]);

  useEffect(() => {
    primaryBtnRef.current?.focus();
  }, [index]);

  const handleNext = () => {
    if (isLast) finish('done');
    else goTo(index + 1);
  };

  const hole = anchorRect
    ? {
        x: Math.max(0, anchorRect.left - HOLE_PADDING),
        y: Math.max(0, anchorRect.top - HOLE_PADDING),
        w: anchorRect.width + HOLE_PADDING * 2,
        h: anchorRect.height + HOLE_PADDING * 2,
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] font-inter"
      role="dialog"
      aria-modal="true"
      aria-label="Kurze Editor-Tour"
      data-testid="editor-coach-tour"
    >
      {/* Abdunklung + Spotlight-Aussparung */}
      <svg className="absolute inset-0 h-full w-full" width="100%" height="100%" aria-hidden="true">
        <defs>
          <mask id="editor-tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hole && <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx="10" fill="black" />}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(17,24,39,0.55)" mask="url(#editor-tour-mask)" />
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx="10"
            fill="none"
            stroke="#10B981"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* Popover */}
      <div
        ref={popoverRef}
        className="absolute rounded-xl bg-white shadow-xl border border-gray-200 p-5"
        style={{ top: pos.top, left: pos.left, width: POPOVER_WIDTH, maxWidth: 'calc(100vw - 24px)' }}
      >
        <p className="text-[11px] font-medium text-gray-400 mb-1">
          Schritt {index + 1} von {steps.length}
        </p>
        <h2 className="text-base font-semibold text-gray-900">{step.title}</h2>
        <p className="text-sm text-gray-600 mt-2 leading-relaxed">{step.body}</p>

        {/* Fortschritts-Punkte */}
        <div className="flex gap-1.5 items-center mt-4" aria-hidden="true">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-200 ${
                i === index ? 'w-4 h-1.5 bg-emerald-500' : 'w-1.5 h-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={() => finish('skipped')}
            className="text-[13px] font-medium text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer p-0"
            data-testid="editor-coach-tour-skip"
          >
            Tour überspringen
          </button>

          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                className="text-[13px] font-medium text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[40px]"
              >
                Zurück
              </button>
            )}
            <button
              ref={primaryBtnRef}
              type="button"
              onClick={handleNext}
              className="text-[13px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg transition-colors min-h-[40px]"
              data-testid="editor-coach-tour-next"
            >
              {isLast ? 'Fertig' : 'Weiter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

EditorCoachTour.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      anchorId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      placement: PropTypes.oneOf(['right', 'bottom']),
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditorCoachTour;
